"use client"

import { useState, useCallback } from "react"
import { CounterService } from "../services/counterService"

export const useStreamingQuote = () => {
  const [quote, setQuote] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const generateQuote = useCallback(async (category?: string) => {
    setIsLoading(true)
    setError(null)
    setQuote("")

    try {
      // Create category-specific prompt
      let prompt =
        "Generate a short, uplifting and inspiring message for someone who needs motivation today. Keep it under 20 words and make it personal and encouraging. Just give me the quote, no additional text. Just give me one sentence, no more than 20 words."

      if (category) {
        switch (category) {
          case "fitness":
            prompt =
              "Generate a short, powerful fitness motivation quote that inspires someone to push through their workout and stay committed to their health goals. Keep it under 20 words. Just give me the quote, no additional text. Just give me one sentence, no more than 20 words."
            break
          case "business":
            prompt =
              "Generate a short, inspiring business motivation quote that encourages entrepreneurship, leadership, and professional success. Keep it under 20 words. Just give me the quote, no additional text. Just give me one sentence, no more than 20 words."
            break
          case "success":
            prompt =
              "Generate a short, motivational quote about achieving success, reaching goals, and personal growth. Keep it under 20 words. Just give me the quote, no additional text. Just give me one sentence, no more than 20 words."
            break
          case "golf":
            prompt =
              "Generate a short, inspiring golf motivation quote that encourages persistence, improvement, mental toughness, and enjoying the game. Focus on overcoming bad shots, staying patient, and continuous improvement. Keep it under 20 words."
              break
          case "calm":
            prompt =
              "Generate a short, original quote that inspires calmness, peace, and serenity. Focus on relaxation, patience, and letting go of stress. Keep it under 20 words. Just give me the quote, no additional text. Just one sentence, no more than 20 words."
            break
          case "humble":
            prompt =
              "Generate a short, original quote that inspires humility and reminds people to stay humble and grounded. Focus on the value of modesty, gratitude, and learning from others. Keep it under 20 words. Just give me the quote, no additional text. Just one sentence, no more than 20 words."
            break
          case "eating":
            prompt =
              "Generate a short, powerful motivation quote that encourages eating healthy, nutritious food and taking care of your body. Focus on healthy choices, avoiding junk, and valuing wellness. Keep it under 20 words. Just give me the quote, no additional text. Just one sentence, no more than 20 words."
            break
          case "study":
            prompt =
            "Generate a short, powerful study motivation quote that encourages focus, discipline, and curiosity. Keep it under 20 words. Just give me the quote, no additional text. Just give me one sentence, no more than 20 words. Just give me the quote, no additional text. Just one sentence, no more than 20 words."
            break
          case "stress":
            prompt =
            "Generate a short, comforting quote to ease stress that promotes breathing, perspective, and letting go. Keep it under 20 words. Just give me the quote, no additional text. Just give me one sentence, no more than 20 words. Just give me the quote, no additional text. Just one sentence, no more than 20 words."
            break
          case "relationship":
            prompt =
            "Generate a short, heartfelt relationship quote that celebrates love, trust, and supportive communication. Keep it under 20 words. Just give me the quote, no additional text. Just give me one sentence, no more than 20 words.  Just give me the quote, no additional text. Just one sentence, no more than 20 words."
            break
          default:
            prompt =
              "Generate a short, uplifting and inspiring message for someone who needs motivation today. Keep it under 20 words and make it personal and encouraging. Just give me the quote, no additional text. Just give me one sentence, no more than 20 words."
        }
      }
      
      // const response = await fetch("https://heedable.com/api/generate", {

      const response = await fetch("https://corsproxy.io/?https://llm.zithera.com.au/api/generate", {
      // const response = await fetch("https://llm.zithera.com.au/api/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "26dYUuR1wA2YUoeJp7Gbn0qeA1vhbqu130GUEvdQ57wontpBEHD0khzM719a05TPXKi6RyLPlOhxCnmW",
        },
        body: JSON.stringify({
          model: "llama3.2",
          prompt: prompt,
        }),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      // Get the raw response text
      const responseText = await response.text()

      // Split by } { pattern (with spaces) and also handle }{
      const jsonStrings = responseText.split(/}\s*{/).map((chunk, index, array) => {
        if (index === 0 && index === array.length - 1) {
          // Single chunk case
          return chunk
        } else if (index === 0) {
          // First chunk
          return chunk + "}"
        } else if (index === array.length - 1) {
          // Last chunk
          return "{" + chunk
        } else {
          // Middle chunks
          return "{" + chunk + "}"
        }
      })

      // Extract all response parts and combine them
      let fullQuote = ""
      for (const jsonString of jsonStrings) {
        try {
          const parsed = JSON.parse(jsonString.trim())
          if (parsed.response) {
            fullQuote += parsed.response
          }
        } catch (e) {
          // Try to extract JSON objects using regex as fallback
          const jsonMatches = jsonString.match(/\{[^}]*\}/g)
          if (jsonMatches) {
            for (const match of jsonMatches) {
              try {
                const parsed = JSON.parse(match)
                if (parsed.response) {
                  fullQuote += parsed.response
                }
              } catch (innerE) {
                console.warn("Skipping invalid JSON:", match)
              }
            }
          }
        }
      }

      if (fullQuote) {
        // Clean up the quote (remove extra quotes if present)
        const cleanQuote = fullQuote.replace(/^"/, "").replace(/"$/, "")

        // Track the quote generation (increment counters)
        await CounterService.trackQuoteGeneration(category)

        // Add typewriter effect
        let currentIndex = 0
        const typewriterInterval = setInterval(() => {
          if (currentIndex < cleanQuote.length) {
            setQuote(cleanQuote.substring(0, currentIndex + 1))
            currentIndex++
          } else {
            clearInterval(typewriterInterval)
          }
        }, 30) // 30ms delay between characters
      } else {
        throw new Error("No response content found")
      }
    } catch (err) {
      console.error("Quote generation failed:", err)

      // Fallback to local quotes
      const fallbackQuotes = getFallbackQuotes(category)
      const randomQuote = fallbackQuotes[Math.floor(Math.random() * fallbackQuotes.length)]

      // Still track the quote generation even for fallback quotes
      await CounterService.trackQuoteGeneration(category)

      // Add typewriter effect for fallback quotes too
      let currentIndex = 0
      const typewriterInterval = setInterval(() => {
        if (currentIndex < randomQuote.length) {
          setQuote(randomQuote.substring(0, currentIndex + 1))
          currentIndex++
        } else {
          clearInterval(typewriterInterval)
        }
      }, 30)

      setError("Using offline quotes - API temporarily unavailable")
    } finally {
      setIsLoading(false)
    }
  }, [])

  return { quote, isLoading, error, generateQuote }
}

// Fallback quotes when API is unavailable
function getFallbackQuotes(category?: string) {
  const quotes = {
    fitness: [
      "The only bad workout is the one that didn't happen.",
      "Your body can do it. It's your mind you have to convince.",
      "Strength doesn't come from what you can do. It comes from overcoming the things you once thought you couldn't.",
      "The pain you feel today will be the strength you feel tomorrow.",
      "Don't wish for it, work for it.",
      "Success starts with self-discipline.",
      "Push yourself because no one else is going to do it for you.",
      "Great things never come from comfort zones.",
      "The only impossible journey is the one you never begin.",
      "Sweat is just fat crying.",
      "Train like a beast, look like a beauty.",
      "Your only limit is your mind.",
      "Fitness is not about being better than someone else. It's about being better than you used to be.",
      "Champions train, losers complain.",
      "The groundwork for all happiness is good health.",
      "Take care of your body. It's the only place you have to live.",
      "A healthy outside starts from the inside.",
      "Exercise is a celebration of what your body can do, not a punishment for what you ate.",
      "The first wealth is health.",
      "Physical fitness is not only one of the most important keys to a healthy body, it is the basis of dynamic and creative intellectual activity.",
      "To enjoy the glow of good health, you must exercise.",
      "Those who think they have no time for exercise will sooner or later have to find time for illness.",
      "The doctor of the future will give no medicine, but will interest his patients in the care of the human frame, in diet, and in the cause and prevention of disease.",
      "Exercise is king. Nutrition is queen. Put them together and you've got a kingdom.",
      "A man's health can be judged by which he takes two at a time - pills or stairs.",
      "The human body is the best picture of the human soul.",
      "Health is a state of complete harmony of the body, mind and spirit.",
      "If you don't make time for exercise, you'll probably have to make time for illness.",
      "Exercise should be regarded as tribute to the heart.",
      "The body achieves what the mind believes.",
      "Fitness is like a relationship. You can't cheat and expect it to work.",
      "Your body is your temple. Keep it pure and clean for the soul to reside in.",
      "The resistance that you fight physically in the gym and the resistance that you fight in life can only build a strong character.",
      "Exercise is done against one's wishes and maintained only because the alternative is worse.",
      "The successful warrior is the average man with laser-like focus.",
      "Discipline is choosing between what you want now and what you want most.",
      "You don't have to be extreme, just consistent.",
      "Progress, not perfection.",
      "Strong is the new skinny.",
      "Sore today, strong tomorrow.",
      "Don't stop when you're tired. Stop when you're done.",
      "The only workout you regret is the one you didn't do.",
      "Excuses don't burn calories.",
      "Your health is an investment, not an expense.",
      "Fitness is not a destination, it's a journey.",
      "The miracle isn't that I finished. The miracle is that I had the courage to start.",
      "Every workout is progress.",
      "Believe in yourself and all that you are. Know that there is something inside you that is greater than any obstacle.",
      "The only person you are destined to become is the person you decide to be.",
      "What seems impossible today will one day become your warm-up.",
      "You are stronger than you think.",
      "Champions don't become champions in the ring. They become champions in their training.",
      "The cave you fear to enter holds the treasure you seek.",
      "Strength grows in the moments when you think you can't go on but you keep going anyway.",
      "Your current body is the only body that can take you to your new body.",
      "Don't limit your challenges, challenge your limits.",
      "The pain of discipline weighs ounces while the pain of regret weighs tons.",
      "If it doesn't challenge you, it won't change you.",
      "You're only one workout away from a good mood.",
      "The hardest lift of all is lifting your butt off the couch.",
      "Sweat is magic. Cover yourself in it daily to grant your wishes.",
      "You don't get what you wish for. You get what you work for.",
      "Strive for progress, not perfection.",
      "A one hour workout is 4% of your day. No excuses.",
      "Don't wish it were easier; wish you were stronger.",
      "Champions keep playing until they get it right.",
      "Success isn't given. It's earned in the gym.",
      "The only way to finish is to start.",
      "Fitness is like marriage. You can't cheat on it and expect it to work.",
      "Health is not about the weight you lose, but about the life you gain.",
      "Exercise is a celebration of what your body can do.",
      "Physical fitness is the first requisite of happiness.",
      "Exercise is medicine.",
      "The only competition is with yourself.",
      "Your body is a reflection of your lifestyle.",
      "Health is the greatest gift.",
      "Exercise is the key not only to physical health but to peace of mind.",
      "A healthy body is a guest chamber for the soul.",
      "To keep the body in good health is a duty.",
      "Health is a relationship between you and your body.",
      "The greatest wealth is health.",
      "Exercise is a dirty word. Every time I hear it, I wash my mouth out with chocolate.",
      "I have two doctors, my left leg and my right.",
      "The only way to keep your health is to eat what you don't want, drink what you don't like, and do what you'd rather not.",
      "Exercise is labor without weariness.",
      "The body benefits from movement, and the mind benefits from stillness.",
      "Take care of your body with steadfast fidelity.",
      "Health is not valued till sickness comes.",
      "A sound mind in a sound body.",
      "The first and best victory is to conquer self.",
      "He who conquers others is strong; He who conquers himself is mighty.",
      "Strength does not come from physical capacity. It comes from an indomitable will.",
      "The will to win, the desire to succeed, the urge to reach your full potential.",
      "Champions aren't made in gyms. Champions are made from something deep inside them.",
      "It is during our darkest moments that we must focus to see the light.",
      "The way to get started is to quit talking and begin doing.",
      "Don't watch the clock; do what it does. Keep going.",
      "The future belongs to those who believe in the beauty of their dreams.",
      "It does not matter how slowly you go as long as you do not stop.",
      "Everything you've ever wanted is on the other side of fear.",
      "Believe you can and you're halfway there.",
      "The only impossible journey is the one you never begin.",
      "In the middle of difficulty lies opportunity.",
      "Life is 10% what happens to you and 90% how you react to it.",
      "The best time to plant a tree was 20 years ago. The second best time is now.",
      "Your limitation—it's only your imagination.",
      "Great things happen to those who don't stop believing, trying, learning, and being grateful.",
      "Dream bigger. Do bigger.",
      "Don't be afraid to give up the good to go for the great.",
      "The future depends on what you do today.",
      "Success is walking from failure to failure with no loss of enthusiasm.",
      "Opportunities don't happen. You create them.",
      "Don't wait for opportunity. Create it.",
      "The difference between ordinary and extraordinary is that little extra.",
      "Success is the sum of small efforts repeated day in and day out.",
      "The only way to do great work is to love what you do.",
      "Success is not about being the best. It's about always getting better.",
      "Champions keep playing until they get it right.",
      "The road to success is always under construction.",
    ],
    business: [
      "Success is not final, failure is not fatal: it is the courage to continue that counts.",
      "The way to get started is to quit talking and begin doing.",
      "Innovation distinguishes between a leader and a follower.",
      "Your limitation—it's only your imagination.",
      "Great things happen to those who don't stop believing, trying, learning, and being grateful.",
      "Dream bigger. Do bigger.",
      "Don't be afraid to give up the good to go for the great.",
      "The future depends on what you do today.",
      "Success is walking from failure to failure with no loss of enthusiasm.",
      "Opportunities don't happen. You create them.",
      "Don't wait for opportunity. Create it.",
      "The best time to plant a tree was 20 years ago. The second best time is now.",
      "A business that makes nothing but money is a poor business.",
      "The customer is why we exist.",
      "Quality is not an act, it is a habit.",
      "The secret of getting ahead is getting started.",
      "Don't be afraid to give up the good to go for the great.",
      "I find that the harder I work, the more luck I seem to have.",
      "Success is the result of preparation, hard work, and learning from failure.",
      "The way to get started is to quit talking and begin doing.",
      "Innovation distinguishes between a leader and a follower.",
      "Stay hungry. Stay foolish.",
      "The people who are crazy enough to think they can change the world are the ones who do.",
      "Your work is going to fill a large part of your life, and the only way to be truly satisfied is to do what you believe is great work.",
      "Don't let yesterday take up too much of today.",
      "You learn more from failure than from success. Don't let it stop you. Failure builds character.",
      "It's not whether you get knocked down, it's whether you get up.",
      "If you are not willing to risk the usual, you will have to settle for the ordinary.",
      "Trust because you are willing to accept the risk, not because it's safe or certain.",
      "Take up one idea. Make that one idea your life - think of it, dream of it, live on that idea.",
      "All our dreams can come true if we have the courage to pursue them.",
      "Good things come to people who wait, but better things come to those who go out and get them.",
      "If you do what you always did, you will get what you always got.",
      "Success is where preparation and opportunity meet.",
      "Don't be afraid to give up the good to go for the great.",
      "I find that the harder I work, the more luck I seem to have.",
      "Success is the result of preparation, hard work, and learning from failure.",
      "Don't be afraid to start over. It's a brand new opportunity to rebuild what you truly want.",
      "The biggest risk is not taking any risk.",
      "Innovation distinguishes between a leader and a follower.",
      "Stay hungry. Stay foolish.",
      "The people who are crazy enough to think they can change the world are the ones who do.",
      "Your work is going to fill a large part of your life, and the only way to be truly satisfied is to do what you believe is great work.",
      "Don't let yesterday take up too much of today.",
      "You learn more from failure than from success. Don't let it stop you. Failure builds character.",
      "It's not whether you get knocked down, it's whether you get up.",
      "If you are not willing to risk the usual, you will have to settle for the ordinary.",
      "Trust because you are willing to accept the risk, not because it's safe or certain.",
      "Take up one idea. Make that one idea your life - think of it, dream of it, live on that idea.",
      "All our dreams can true if we have the courage to pursue them.",
      "Good things come to people who wait, but better things come to those who go out and get them.",
      "If you do what you always did, you will get what you always got.",
      "Success is where preparation and opportunity meet.",
      "Don't be afraid to give up the good to go for the great.",
      "I find that the harder I work, the more luck I seem to have.",
      "Success is the result of preparation, hard work, and learning from failure.",
      "Don't be afraid to start over. It's a brand new opportunity to rebuild what you truly want.",
      "The biggest risk is not taking any risk.",
      "Innovation distinguishes between a leader and a follower.",
      "Stay hungry. Stay foolish.",
      "The people who are crazy enough to think they can change the world are the ones who do.",
      "Your work is going to fill a large part of your life, and the only way to be truly satisfied is to do what you believe is great work.",
      "Don't let yesterday take up too much of today.",
      "You learn more from failure than from success. Don't let it stop you. Failure builds character.",
      "It's not whether you get knocked down, it's whether you get up.",
      "If you are not willing to risk the usual, you will have to settle for the ordinary.",
      "Trust because you are willing to accept the risk, not because it's safe or certain.",
      "Take up one idea. Make that one idea your life - think of it, dream of it, live on that idea.",
      "All our dreams can come true if we have the courage to pursue them.",
      "Good things come to people who wait, but better things come to those who go out and get them.",
      "If you do what you always did, you will get what you always got.",
      "Success is where preparation and opportunity meet.",
      "Don't be afraid to give up the good to go for the great.",
      "I find that the harder I work, the more luck I seem to have.",
      "Success is the result of preparation, hard work, and learning from failure.",
      "Don't be afraid to start over. It's a brand new opportunity to rebuild what you truly want.",
    ],
    success: [
      "Success is not the key to happiness. Happiness is the key to success.",
      "Don't be pushed around by the fears in your mind. Be led by the dreams in your heart.",
      "The difference between ordinary and extraordinary is that little extra.",
      "Success is the sum of small efforts repeated day in and day out.",
      "Believe you can and you're halfway there.",
      "The only way to do great work is to love what you do.",
      "Success is not about being the best. It's about always getting better.",
      "Champions keep playing until they get it right.",
      "The road to success is always under construction.",
      "Success is not just about what you accomplish in your life, it's about what you inspire others to do.",
      "The harder you work for something, the greater you'll feel when you achieve it.",
      "Don't be afraid to give up the good to go for the great.",
      "Success comes to those who dare to begin.",
      "The secret of success is to do the common thing uncommonly well.",
      "Success is not final, failure is not fatal: it is the courage to continue that counts.",
      "The way to get started is to quit talking and begin doing.",
      "Don't watch the clock; do what it does. Keep going.",
      "The future belongs to those who believe in the beauty of their dreams.",
      "It does not matter how slowly you go as long as you do not stop.",
      "Everything you've ever wanted is on the other side of fear.",
      "The only impossible journey is the one you never begin.",
      "In the middle of difficulty lies opportunity.",
      "Life is 10% what happens to you and 90% how you react to it.",
      "The best time to plant a tree was 20 years ago. The second best time is now.",
      "Your limitation—it's only your imagination.",
      "Great things happen to those who don't stop believing, trying, learning, and being grateful.",
      "Dream bigger. Do bigger.",
      "The future depends on what you do today.",
      "Success is walking from failure to failure with no loss of enthusiasm.",
      "Opportunities don't happen. You create them.",
      "Don't wait for opportunity. Create it.",
      "Be yourself; everyone else is already taken.",
      "You miss 100% of the shots you don't take.",
      "Whether you think you can or you think you can't, you're right.",
      "The only person you are destined to become is the person you decide to be.",
      "What lies behind us and what lies before us are tiny matters compared to what lies within us.",
      "The best revenge is massive success.",
      "I have not failed. I've just found 10,000 ways that won't work.",
      "A person who never made a mistake never tried anything new.",
      "The person who says it cannot be done should not interrupt the person who is doing it.",
      "There are no traffic jams along the extra mile.",
      "It is never too late to be what you might have been.",
      "A goal is a dream with a deadline.",
      "The only way to achieve the impossible is to believe it is possible.",
      "Don't be afraid of change. You may lose something good, but you may gain something even better.",
      "The biggest risk is not taking any risk.",
      "Innovation distinguishes between a leader and a follower.",
      "Stay hungry. Stay foolish.",
      "The people who are crazy enough to think they can change the world are the ones who do.",
      "Your work is going to fill a large part of your life, and the only way to be truly satisfied is to do what you believe is great work.",
      "All our dreams can come true if we have the courage to pursue them.",
      "Good things come to people who wait, but better things come to those who go out and get them.",
      "If you do what you always did, you will get what you always got.",
      "Success is where preparation and opportunity meet.",
      "I find that the harder I work, the more luck I seem to have.",
      "Success is the result of preparation, hard work, and learning from failure.",
      "Don't be afraid to start over. It's a brand new opportunity to rebuild what you truly want.",
      "Success is not measured by what you accomplish, but by the opposition you have encountered, and the courage with which you have maintained the struggle against overwhelming odds.",
      "The successful warrior is the average man with laser-like focus.",
      "Success is liking yourself, liking what you do, and liking how you do it.",
      "Success is not about the destination, it's about the journey.",
      "The price of success is hard work, dedication to the job at hand, and the determination that whether we win or lose, we have applied the best of ourselves to the task at hand.",
      "Success is not about reaching the top. It's about how you make a positive difference to the world.",
      "Success is not the absence of failure; it's the persistence through failure.",
      "Success is not about being perfect. It's about being better than you were yesterday.",
      "The most successful people are those who are good at plan B.",
      "Success is not about luck. It's about preparation meeting opportunity.",
      "Success is not about what you have, but who you become in the process of achieving it.",
      "The road to success is dotted with many tempting parking spaces.",
      "Success is not a destination, but the road that you're on. Being successful means that you're working hard and walking your walk every day. You can only live your dream by working hard towards it. That's living your dream.",
      "Success is not about being the smartest person in the room. It's about being willing to learn from everyone in the room.",
      "Success is not about avoiding failure. It's about learning from failure and using it as a stepping stone to success.",
      "The difference between a successful person and others is not a lack of strength, not a lack of knowledge, but rather a lack of will.",
      "Success is not about being fearless. It's about being afraid and doing it anyway.",
      "Success is not about having all the answers. It's about asking the right questions.",
      "Success is not about being the best. It's about being better than you were yesterday.",
      "The key to success is to focus our conscious mind on things we desire not things we fear.",
      "Success is not about the money you make. It's about the difference you make in people's lives.",
      "Success is not about reaching the top. It's about how many people you bring with you.",
      "Success is not about being perfect. It's about being authentic.",
      "The secret to success is constancy to purpose.",
      "Success is not about what you accomplish for yourself. It's about what you inspire others to do.",
      "Success is not about being the fastest. It's about never giving up.",
      "Success is not about having everything. It's about making the most of what you have.",
      "The foundation of success is good health: mental, physical, emotional, spiritual.",
      "Success is not about being lucky. It's about being prepared when luck meets opportunity.",
      "Success is not about being right all the time. It's about being willing to admit when you're wrong and learn from it.",
      "Success is not about being popular. It's about being respected.",
      "Success is not about being comfortable. It's about being willing to step outside your comfort zone.",
      "The measure of success is not whether you have a tough problem to deal with, but whether it is the same problem you had last year.",
      "Success is not about being the loudest voice in the room. It's about being the most thoughtful.",
      "Success is not about being busy. It's about being productive.",
      "Success is not about being perfect. It's about being consistent.",
      "Success is not about being the first. It's about being the best.",
      "The secret of success is learning how to use pain and pleasure instead of having pain and pleasure use you. If you do that, you're in control of your life. If you don't, life controls you.",
      "Success is not about being rich. It's about being fulfilled.",
      "Success is not about being famous. It's about being significant.",
      "Success is not about being powerful. It's about being influential.",
      "Success is not about being independent. It's about being interdependent.",
      "The road to success and the road to failure are almost exactly the same.",
      "Success is not about being perfect. It's about being persistent.",
      "Success is not about being the strongest. It's about being the most resilient.",
      "Success is not about being the smartest. It's about being the most adaptable.",
      "Success is not about being the fastest. It's about being the most consistent.",
      "The key to success is to start before you are ready.",
      "Success is not about being fearless. It's about facing your fears.",
      "Success is not about being confident. It's about being competent.",
      "Success is not about being lucky. It's about being prepared.",
      "Success is not about being talented. It's about being dedicated.",
      "The price of success is hard work, dedication to the job at hand.",
      "Success is not about being perfect. It's about being willing to improve.",
      "Success is not about being the best. It's about doing your best.",
      "Success is not about being right. It's about being effective.",
      "Success is not about being popular. It's about being valuable.",
      "The secret to success is to know something nobody else knows.",
      "Success is not about being comfortable. It's about being challenged.",
      "Success is not about being safe. It's about being brave.",
      "Success is not about being certain. It's about being willing to take risks.",
      "Success is not about being perfect. It's about being real.",
    ],
    golf: [
      "Golf is a game of inches. The most important are the six inches between your ears.",
      "The most important shot in golf is the next one.",
      "Golf is not a game of perfect. It's about managing your misses.",
      "Every shot makes someone happy. If it's not you, it's your opponent.",
      "The harder you practice, the luckier you get on the course.",
      "Golf teaches you patience, persistence, and humility - lessons for life.",
      "A bad day on the golf course is still better than a good day at the office.",
      "Golf is a game where you can always improve, no matter your age or skill level.",
      "The secret to golf is to turn three shots into two whenever possible.",
      "Golf is played between the ears. Master your mind, master your game.",
      "Every golfer has two handicaps: their official one and their mental one.",
      "The beauty of golf is that you're always chasing perfection, knowing you'll never catch it.",
      "Golf is a game of recovery. How you bounce back from a bad shot defines your round.",
      "Practice doesn't make perfect in golf. Perfect practice makes perfect.",
      "Golf is like life: it's not about the score, it's about how you play the game.",
      "The golf course is the ultimate teacher of patience and perseverance.",
      "Every round of golf is a new opportunity to improve and learn something new.",
      "Golf rewards those who stay calm under pressure and trust their swing.",
      "The difference between a good golfer and a great golfer is mental toughness.",
      "Golf is a game where you compete against yourself more than anyone else.",
      "A positive attitude on the golf course can turn a bad round into a learning experience.",
      "Golf teaches you that even the best players in the world have bad days.",
      "The key to golf is not avoiding mistakes, but minimizing their impact.",
      "Golf is a journey, not a destination. Enjoy every step of the way.",
      "Every golfer knows that the next shot could be the best one they've ever hit.",
      "Golf is a game of constant adjustment and adaptation.",
      "The golf course doesn't care about your excuses, only your execution.",
      "Golf teaches you to focus on what you can control and let go of what you can't.",
      "A good golf swing is built on fundamentals, but a great round is built on mental strength.",
      "Golf is the closest game to the game we call life. You get bad breaks, you get good breaks.",
      "The golf course is where you learn that perfection is not the goal, improvement is.",
      "Golf rewards preparation, punishes complacency, and teaches humility.",
      "Every golfer has the potential to hit the shot of their life on any given swing.",
      "Golf is a game where you can always find something to work on and improve.",
      "The golf course is a classroom where every shot teaches you something new.",
      "Golf is not about being perfect, it's about being better than you were yesterday.",
      "A true golfer never gives up, no matter how many strokes behind they are.",
      "Golf teaches you that consistency beats perfection every time.",
      "The golf course is where you learn to celebrate small victories and learn from defeats.",
      "Golf is a game where your biggest opponent is often yourself.",
      "Every round of golf is a chance to write a new story and create new memories.",
      "Golf teaches you that the journey of improvement is more rewarding than the destination.",
      "The golf course is where you learn that patience and persistence pay off.",
      "Golf is a game where you can always surprise yourself with what you're capable of.",
      "A good golfer knows that every shot is independent of the last one.",
      "Golf teaches you to stay in the present moment and focus on the task at hand.",
      "The golf course is where you learn that mental toughness is just as important as physical skill.",
      "Golf is a game where you learn to embrace the challenge and enjoy the process.",
      "Every golfer knows that the game is won or lost between the ears.",
      "Golf teaches you that setbacks are setups for comebacks.",
      "The golf course is where you learn that confidence comes from preparation and practice.",
      "Golf is a game where you can always find a reason to keep playing and improving.",
      "A true golfer understands that the game is about more than just the score.",
      "Golf teaches you that every shot is a new opportunity to succeed.",
      "The golf course is where you learn that persistence and patience are virtues.",
      "Golf is a game where you learn to trust your instincts and commit to your decisions.",
      "Every golfer has experienced the magic of a perfectly struck shot that keeps them coming back.",
      "Golf teaches you that improvement comes from embracing challenges, not avoiding them.",
      "The golf course is where you learn that mental preparation is just as important as physical preparation.",
      "Golf is a game where you can always find something beautiful, even on the worst days.",
      "A good golfer knows that every round is a chance to learn something new about themselves.",
      "Golf teaches you that the only way to get better is to keep playing and keep learning.",
      "The golf course is where you learn that success is measured by progress, not perfection.",
      "Golf is a game where you learn to appreciate the journey as much as the destination.",
      "Every golfer knows that the game has a way of humbling you just when you think you've got it figured out.",
      "Golf teaches you that resilience and determination are more valuable than natural talent.",
      "The golf course is where you learn that every shot matters, but no single shot defines you.",
      "Golf is a game where you can always find hope, even in the most challenging situations.",
      "A true golfer understands that the game is about continuous improvement, not instant gratification.",
      "Golf teaches you that the best rounds often come when you least expect them.",
      "The golf course is where you learn that focus and concentration are skills that can be developed.",
      "Golf is a game where you learn to celebrate progress and learn from setbacks.",
      "Every golfer has the potential to achieve something great if they never give up trying.",
      "Golf teaches you that the game is as much about character as it is about skill.",
      "The golf course is where you learn that every day is a new opportunity to improve.",
      "Golf is a game where you can always find a reason to smile, even after a bad shot.",
      "A good golfer knows that the game is about managing expectations and staying positive.",
      "Golf teaches you that the most important competition is the one you have with yourself.",
      "The golf course is where you learn that patience and persistence always pay off in the end.",
      "Golf is a game where you learn that every shot is a chance to create something special.",
      "Every golfer understands that the game teaches life lessons that extend far beyond the course.",
      "Golf teaches you that improvement is a journey, not a destination.",
      "The golf course is where you learn that mental strength is just as important as physical ability.",
      "Golf is a game where you can always find something to be grateful for.",
      "A true golfer knows that the game is about enjoying the process, not just the results.",
      "Golf teaches you that every round is a new adventure with unlimited possibilities.",
      "The golf course is where you learn that consistency and dedication lead to success.",
      "Golf is a game where you learn to embrace both the highs and the lows.",
      "Every golfer has the opportunity to create their own success story, one shot at a time.",
      "Golf teaches you that the game is about more than just hitting a ball into a hole.",
      "The golf course is where you learn that every shot is a chance to prove something to yourself.",
      "Golf is a game where you can always find inspiration to keep improving and never give up.",
    ],
    healthy: [
      "Eat to fuel your body, not to feed your emotions.",
      "Your body deserves food that loves it back.",
      "Healthy eating is a form of self-respect.",
      "Invest in your body; it'll pay you back in energy and happiness.",
      "You are what you eat—choose wisely.",
      "Eat clean, feel great, live well.",
      "Nourish to flourish.",
      "Every meal is a chance to love your body.",
      "Don’t dig your grave with your own knife and fork.",
      "A healthy outside starts from the inside.",
      "Good food is wise medicine.",
      "When you eat better, you feel better.",
      "Healthy food fuels positive vibes.",
      "Don't count calories, make calories count.",
      "Eat food that makes you thrive, not just survive.",
      "Healthy habits create a healthy life.",
      "Skip the junk, cherish your health.",
      "The food you eat can be either the safest medicine or the slowest poison.",
      "Real food doesn’t have ingredients. Real food is ingredients.",
      "Eat right, live bright."
    ],
    humble: [
      "Humility is not thinking less of yourself, it's thinking of yourself less.",
      "True humility is staying teachable, regardless of how much you already know.",
      "Humility is the foundation of all virtues.",
      "The greatest wisdom is in knowing that you know nothing.",
      "Humility is not denying your strengths, but being honest about your weaknesses.",
      "A humble person is not one who thinks less of themselves, but one who thinks of themselves less.",
      "The more you know, the more you realize how much you don't know.",
      "Stay humble, work hard, and be kind.",
      "Greatness begins with humility.",
      "Let your actions speak louder than your ego.",
      "Pride makes us artificial; humility makes us real.",
      "A true leader is humble and grateful.",
      "Never let success get to your head or failure to your heart.",
      "The higher we are placed, the more humbly we should walk.",
      "There is beauty in a humble spirit.",
      "Be teachable, you’re not always right.",
      "A wise man is humble but never weak.",
      "True strength lies in humility.",
      "Stay grounded, even when you rise.",
      "Humility is the solid foundation of all virtue.",
      "No one ever choked on swallowing their pride.",
      "Gratitude and humility light the path to wisdom.",
      "The smallest act of kindness is worth more than the grandest intention.",
      "Admit when you are wrong, and learn from it.",
      "Humble hearts make the biggest impact.",
      "Be proud of your progress, but humble in your journey.",
      "Great souls are humble souls.",
    ],
    calm: [
      "Inhale peace, exhale stress.",
      "Let calm be your superpower.",
      "Find serenity in the silence within.",
      "Stay calm; storms always pass.",
      "Peace begins the moment you let go of fear.",
      "Breathe deeply and let go.",
      "Be still, be present, be calm.",
      "Your calm mind is the ultimate weapon against stress.",
      "Don’t let the chaos around you disturb your inner peace.",
      "Where calmness reigns, clarity follows.",
      "Serenity is not freedom from the storm, but peace within the storm.",
      "Still waters run deep.",
      "Patience brings peace.",
      "Sometimes, the most productive thing you can do is relax.",
      "Calm is a decision, not a circumstance.",
      "Let your heart be still.",
      "A calm mind brings inner strength.",
      "You cannot always control what goes on outside, but you can always control what goes on inside.",
      "Respond with calm, not chaos.",
      "Let calmness be your compass."
    ],
    general: [
      "The only way to do great work is to love what you do.",
      "Life is what happens to you while you're busy making other plans.",
      "The future belongs to those who believe in the beauty of their dreams.",
      "It is during our darkest moments that we must focus to see the light.",
      "Believe you can and you're halfway there.",
      "The only impossible journey is the one you never begin.",
      "In the middle of difficulty lies opportunity.",
      "Life is 10% what happens to you and 90% how you react to it.",
      "The best time to plant a tree was 20 years ago. The second best time is now.",
      "Be yourself; everyone else is already taken.",
      "You miss 100% of the shots you don't take.",
      "Whether you think you can or you think you can't, you're right.",
      "The only person you are destined to become is the person you decide to be.",
      "What lies behind us and what lies before us are tiny matters compared to what lies within us.",
      "The best revenge is massive success.",
      "I have not failed. I've just found 10,000 ways that won't work.",
      "A person who never made a mistake never tried anything new.",
      "The person who says it cannot be done should not interrupt the person who is doing it.",
      "There are no traffic jams along the extra mile.",
      "It is never too late to be what you might have been.",
      "A goal is a dream with a deadline.",
      "The only way to achieve the impossible is to believe it is possible.",
      "Don't be afraid of change. You may lose something good, but you may gain something even better.",
      "The biggest risk is not taking any risk.",
      "Innovation distinguishes between a leader and a follower.",
      "Stay hungry. Stay foolish.",
      "The people who are crazy enough to think they can change the world are the ones who do.",
      "Your work is going to fill a large part of your life, and the only way to be truly satisfied is to do what you believe is great work.",
      "Don't let yesterday take up too much of today.",
      "You learn more from failure than from success. Failure builds character.",
      "It's not whether you get knocked down, it's whether you get up.",
      "If you are not willing to risk the usual, you will have to settle for the ordinary.",
      "Trust because you are willing to accept the risk, not because it's safe or certain.",
      "Take up one idea. Make that one idea your life - think of it, dream of it, live on that idea.",
      "All our dreams can come true if we have the courage to pursue them.",
      "Good things come to people who wait, but better things come to those who go out and get them.",
      "If you do what you always did, you will get what you always got.",
      "Success is where preparation and opportunity meet.",
      "Don't be afraid to give up the good to go for the great.",
      "I find that the harder I work, the more luck I seem to have.",
      "Success is the result of preparation, hard work, and learning from failure.",
      "Don't be afraid to start over. It's a brand new opportunity to rebuild what you truly want.",
      "Life isn't about finding yourself. Life is about creating yourself.",
      "The purpose of our lives is to be happy.",
      "Life is really simple, but we insist on making it complicated.",
      "The unexamined life is not worth living.",
      "Turn your wounds into wisdom.",
      "The way I see it, if you want the rainbow, you gotta put up with the rain.",
      "Do all the good you can, for all the people you can, in all the ways you can, as long as you can.",
      "Don't judge each day by the harvest you reap but by the seeds that you plant.",
      "The best way to predict the future is to create it.",
      "Life is 10% what happens to you and 90% how you react to it.",
      "The only way to do great work is to love what you do.",
      "If you look at what you have in life, you'll always have more.",
      "Life is what we make it, always has been, always will be.",
      "The purpose of life is not to be happy. It is to be useful, to be honorable, to be compassionate, to have it make some difference that you have lived and lived well.",
      "Life is about making an impact, not making an income.",
      "The meaning of life is to find your gift. The purpose of life is to give it away.",
      "Life is not measured by the number of breaths we take, but by the moments that take our breath away.",
      "The good life is one inspired by love and guided by knowledge.",
      "Life is a journey that must be traveled no matter how bad the roads and accommodations.",
      "Life is really simple, but men insist on making it complicated.",
      "The purpose of our lives is to be happy.",
      "Life isn't about getting and having, it's about giving and being.",
      "The way to get started is to quit talking and begin doing.",
      "Don't watch the clock; do what it does. Keep going.",
      "Everything you've ever wanted is on the other side of fear.",
      "Your limitation—it's only your imagination.",
      "Great things happen to those who don't stop believing, trying, learning, and being grateful.",
      "Dream bigger. Do bigger.",
      "The future depends on what you do today.",
      "Opportunities don't happen. You create them.",
      "Don't wait for opportunity. Create it.",
      "The difference between ordinary and extraordinary is that little extra.",
      "Happiness is not something ready made. It comes from your own actions.",
      "The only true wisdom is in knowing you know nothing.",
      "Life is what happens when you're busy making other plans.",
      "In the end, we will remember not the words of our enemies, but the silence of our friends.",
      "The only way to make sense out of change is to plunge into it, move with it, and join the dance.",
      "Yesterday is history, tomorrow is a mystery, today is a gift of God, which is why we call it the present.",
      "The best and most beautiful things in the world cannot be seen or even touched - they must be felt with the heart.",
      "It does not matter how slowly you go as long as you do not stop.",
      "Our greatest glory is not in never falling, but in rising every time we fall.",
      "The man who moves a mountain begins by carrying away small stones.",
      "It is better to be hated for what you are than to be loved for what you are not.",
      "We are what we repeatedly do. Excellence, then, is not an act, but a habit.",
      "The only thing necessary for the triumph of evil is for good men to do nothing.",
      "Ask not what your country can do for you – ask what you can do for your country.",
      "The only thing we have to fear is fear itself.",
      "That which does not kill us makes us stronger.",
      "I think, therefore I am.",
      "The unexamined life is not worth living.",
      "To be yourself in a world that is constantly trying to make you something else is the greatest accomplishment.",
      "Two things are infinite: the universe and human stupidity; and I'm not sure about the universe.",
      "A room without books is like a body without a soul.",
      "You only live once, but if you do it right, once is enough.",
      "Be the change that you wish to see in the world.",
      "In three words I can sum up everything I've learned about life: it goes on.",
      "If you want to know what a man's like, take a good look at how he treats his inferiors, not his equals.",
      "Don't walk in front of me… I may not follow. Don't walk behind me… I may not lead. Walk beside me… just be my friend.",
      "No one can make you feel inferior without your consent.",
      "If you tell the truth, you don't have to remember anything.",
      "A friend is someone who knows all about you and still loves you.",
      "Always forgive your enemies; nothing annoys them so much.",
      "Live as if you were to die tomorrow. Learn as if you were to live forever.",
      "Darkness cannot drive out darkness: only light can do that. Hate cannot drive out hate: only love can do that.",
      "We accept the love we think we deserve.",
      "Without music, life would be a mistake.",
      "I am so clever that sometimes I don't understand a single word of what I am saying.",
      "To live is the rarest thing in the world. Most people just exist.",
      "We are all in the gutter, but some of us are looking at the stars.",
      "It is not a lack of love, but a lack of friendship that makes unhappy marriages.",
      "The opposite of love is not hate, it's indifference.",
      "It is better to be hated for what you are than to be loved for what you are not.",
      "I have not failed. I've just found 10,000 ways that won't work.",
      "A woman is like a tea bag; you never know how strong it is until it's in hot water.",
      "Insanity is doing the same thing, over and over again, but expecting different results.",
      "There are only two ways to live your life. One is as though nothing is a miracle. The other is as though everything is a miracle.",
      "The person, be it gentleman or lady, who has not pleasure in a good novel, must be intolerably stupid.",
      "Imperfection is beauty, madness is genius and it's better to be absolutely ridiculous than absolutely boring.",
      "Try not to become a person of success, but rather try to become a person of value.",
      "Great minds discuss ideas; average minds discuss events; small minds discuss people.",
    ],
  }

  return quotes[category as keyof typeof quotes] || quotes.general
}
